import { useRef, useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChevronDown } from "../icons";
import { useDismissOnOutsideClick } from "../hooks/useDismissOnOutsideClick";
import { PageBarViewSelectProps } from "./PageBarViewSelect.types";

/**
 * View picker for entity page bars. Always rendered, so the page bar's shape
 * doesn't shift when an entity gains a second view.
 */
export function PageBarViewSelect({
  views,
  selectedKey,
  onSelect,
  onOrderViews,
}: PageBarViewSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useDismissOnOutsideClick(containerRef, open, () => setOpen(false));

  if (views.length === 0) {
    return null;
  }

  const selected =
    views.find((view) => view.key === selectedKey) ?? views[0];

  return (
    <div className="position-relative" ref={containerRef}>
      <button
        type="button"
        title="Change view"
        className="border rounded-2 bg-white hover-bg-light d-flex align-items-center gap-2 header-bar-view-select"
        onClick={() => setOpen((previous) => !previous)}
      >
        <span className="text-muted">View:</span>
        {selected.label}
        <FontAwesomeIcon icon={ChevronDown} />
      </button>
      {open && (
        <div className="header-bar-menu">
          {views.map((view) => (
            <button
              key={view.key}
              type="button"
              title={
                view.deactivated
                  ? "Switched off - records do not open on this view"
                  : undefined
              }
              className={classNames(
                "header-bar-menu-item",
                view.key === selected.key && "fw-bold",
                view.deactivated && "text-body-secondary"
              )}
              onClick={() => {
                onSelect(view.key);
                setOpen(false);
              }}
            >
              {view.label}
              {view.deactivated && (
                <span className="ms-2 badge bg-warning-subtle text-warning-emphasis">
                  Inactive
                </span>
              )}
            </button>
          ))}
          {onOrderViews && (
            <>
              <div className="header-bar-menu-divider" />
              <button
                type="button"
                className="header-bar-menu-item"
                onClick={() => {
                  onOrderViews();
                  setOpen(false);
                }}
              >
                Order views
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default PageBarViewSelect;
